import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  StatCard,
  Card,
  CardContent,
  PageHeader,
  Spinner,
} from '@theonefamily/ui';
import {
  Navigation,
  Clock,
  Timer,
  Hourglass,
  Plus,
  X,
  ArrowUp,
  ArrowDown,
  Minus,
} from 'lucide-react';

interface DispatchStats {
  today_count: number;
  active: number;
  avg_drive_minutes: number;
  by_status: { status: string; count: number }[];
}

interface Dispatch {
  id: string;
  dispatch_number: string;
  status: string;
  priority: string;
  client_name: string;
  vehicle_name: string;
  driver_name: string;
  estimated_drive_minutes: number;
  created_at: string;
}

interface DispatchList {
  dispatches: Dispatch[];
}

const MOCK_STATS: DispatchStats = {
  today_count: 8,
  active: 3,
  avg_drive_minutes: 22,
  by_status: [],
};

const MOCK_LIST: DispatchList = { dispatches: [] };

const STATUS_COLORS: Record<string, { bg: string; text: string }> = {
  pending: { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF' },
  suggested: { bg: 'rgba(59,130,246,0.15)', text: '#3B82F6' },
  accepted: { bg: 'rgba(16,185,129,0.15)', text: '#10B981' },
  in_transit: { bg: 'rgba(234,179,8,0.15)', text: '#EAB308' },
  arrived: { bg: 'rgba(249,115,22,0.15)', text: '#F97316' },
  completed: { bg: 'rgba(34,197,94,0.15)', text: '#22C55E' },
  cancelled: { bg: 'rgba(239,68,68,0.15)', text: '#EF4444' },
};

const PRIORITY_ICONS: Record<string, React.ComponentType<{ className?: string; style?: React.CSSProperties }>> = {
  high: ArrowUp,
  medium: Minus,
  low: ArrowDown,
};

export default function DispatchPage() {
  const { data: statsData, isLoading } = useApi<DispatchStats>('/api/dispatch/stats');
  const { data: listData } = useApi<DispatchList>('/api/dispatch?status=accepted,in_transit,arrived');
  const stats = statsData ?? MOCK_STATS;
  const list = listData ?? MOCK_LIST;
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    client_address: '',
    client_lat: '',
    client_lng: '',
    priority: 'medium',
    required_vehicle_type: 'sedan',
    notes: '',
  });

  const pending = stats.by_status.find((s) => s.status === 'pending')?.count ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Smart Dispatch"
        description="AI-powered dispatch management"
        actions={
          <div className="flex items-center gap-3">
            {isLoading && <Spinner size={16} />}
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              style={{ background: '#10B981', color: '#fff' }}
            >
              <Plus className="w-4 h-4" />
              Create Dispatch
            </button>
          </div>
        }
      />

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Navigation} label="Active Dispatches" value={stats.active} accent="success" />
        <StatCard icon={Clock} label="Today's Dispatches" value={stats.today_count} />
        <StatCard icon={Timer} label="Avg Drive Time" value={`${stats.avg_drive_minutes} min`} />
        <StatCard icon={Hourglass} label="Pending" value={pending} accent="warning" />
      </div>

      {/* Dispatch List */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
            <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>
              Active Dispatches
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  {['Dispatch #', 'Status', 'Priority', 'Client', 'Vehicle', 'Driver', 'Est. Drive', 'Created'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.dispatches.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm" style={{ color: 'var(--muted-foreground)' }}>
                      No active dispatches
                    </td>
                  </tr>
                ) : (
                  list.dispatches.map((d) => {
                    const scEntry = STATUS_COLORS[d.status] ?? { bg: 'rgba(156,163,175,0.15)', text: '#9CA3AF' };
                    const PIcon = PRIORITY_ICONS[d.priority] ?? Minus;
                    return (
                      <tr key={d.id} style={{ borderBottom: '1px solid var(--glass-border)' }}>
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: 'var(--foreground)' }}>{d.dispatch_number}</td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: scEntry.bg, color: scEntry.text }}>
                            {d.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <PIcon className="w-4 h-4" style={{ color: d.priority === 'high' ? '#EF4444' : d.priority === 'low' ? '#3B82F6' : 'var(--muted-foreground)' }} />
                        </td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{d.client_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{d.vehicle_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--foreground)' }}>{d.driver_name}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--muted-foreground)' }}>{d.estimated_drive_minutes} min</td>
                        <td className="px-4 py-3 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                          {new Date(d.created_at).toLocaleString()}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Dispatch Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div
            className="w-full max-w-lg rounded-xl p-6 space-y-4"
            style={{
              background: 'var(--glass-bg)',
              border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(var(--glass-blur))',
            }}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--foreground)' }}>Create Dispatch</h3>
              <button type="button" onClick={() => setShowModal(false)}>
                <X className="w-5 h-5" style={{ color: 'var(--muted-foreground)' }} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Client Name</label>
                <input
                  type="text"
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Client Address</label>
                <input
                  type="text"
                  value={form.client_address}
                  onChange={(e) => setForm({ ...form, client_address: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Latitude</label>
                  <input
                    type="text"
                    value={form.client_lat}
                    onChange={(e) => setForm({ ...form, client_lat: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Longitude</label>
                  <input
                    type="text"
                    value={form.client_lng}
                    onChange={(e) => setForm({ ...form, client_lng: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Priority</label>
                  <select
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Vehicle Type</label>
                  <select
                    value={form.required_vehicle_type}
                    onChange={(e) => setForm({ ...form, required_vehicle_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                  >
                    <option value="sedan">Sedan</option>
                    <option value="suv">SUV</option>
                    <option value="van">Van</option>
                    <option value="truck">Truck</option>
                    <option value="box_truck">Box Truck</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: 'var(--muted-foreground)' }}>Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--foreground)' }}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm"
                style={{ color: 'var(--muted-foreground)', border: '1px solid var(--glass-border)' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 rounded-lg text-sm font-medium"
                style={{ background: '#10B981', color: '#fff' }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
